import Highcharts from 'highcharts'

export interface IMetaBase {
  created: number
  creator: string
  updated?: number
  updatedBy?: string
}

export type Quad = {
  x1: number
  /**
   * the y coordinate of the lower-left point
  */
  y1: number
  /**
   * the x coordinate of the lower-right point
  */
  x2: number
  /**
   * the y coordinate of the lower-right point
  */
  y2: number
  /**
   * the x coordinate of the upper-right point
  */
  x3: number
  /**
   * the y coordinate of the upper-right point
  */
  y3: number
  /**
   * the x coordinate of the upper-left point
  */
  x4: number
  /**
   * the y coordinate of the upper-left point
  */
  y4: number
}

export interface IMeta {
  created: number
  createdBy: string
  updated: number
  updatedBy: string
}

export interface IUser {
  company?: string
  created: number
  dataPrivacy?: ThirdPartyProviderCredentialsRecord
  firstName: string
  fullName?: string
  id: string
  isAdmin?: boolean
  isSuperAdmin?: boolean
  lastName: string
  meta?: IUserMeta
  password?: string
  phone?: string
  phoneVerified?: boolean
  stripe_customer_id?: string
  title?: string
  twoFactorMethod?: MfaMethod
  canRecordVideos?: boolean
  // TOTP two-factor (aristotle-backend). `isTwoFactorEnabled` reflects whether
  // an authenticator app is currently active for the account.
  isTwoFactorEnabled?: boolean
  hasSeenTwoFactorSetup?: boolean
  username?: string
}

export interface IDocumentVersion {
  creator: string
  created: number
  id: string
  title?: string
}

export enum QuantityType {
  Number,
  Currency,
  Date,
  String,
}

export interface IQuantityAggregationQuantity {
  quantityUnits: string
  aggregates: {
    avg: number
    min: number
    max: number
    sum: number
  }
}

export interface IQuantityAggregation {
  termType: ITerm
  quantities: IQuantityAggregationQuantity[]
}

export interface IKeyTermQuantityUnit {
  title: string
}

export type KeyTermFrequency = 'none' | 'hour' | 'day' | 'biweekly' | 'week' | 'month' | 'quarter' | 'year'

export interface IKeyTermQuantityDateValue {
  day?: number
  month?: number
  year?: number
}

export interface IKeyTermQuantityTypeValueRegister {
  [QuantityType.Currency]: number
  [QuantityType.Date]: IKeyTermQuantityDateValue
  [QuantityType.Number]: number
  [QuantityType.String]: string
}

export interface IKeyTermQuantity<T extends keyof IKeyTermQuantityTypeValueRegister> {
  frequency?: KeyTermFrequency
  id: string
  type: T
  units?: string
  value?: IKeyTermQuantityTypeValueRegister[T]
  version?: number
}

// export interface IKeyTermQuantity<T = number> extends IKeyTermQuantityBase<T> {
//   stringValue?: string
// }

export interface IKeyTerm extends IDocumentInfo {
  attribute?: boolean
  autoExtracted?: boolean // Indicates if the term was auto extracted from the document
  highlightColor?: string // Stored as hex color
  pdfPageNumber?: number
  position?: number // The zero-based position of the original text in the document.
  quads?: Quad[]
  title?: string
  versionId?: string
}

export interface IDocumentInfo {
  content: string
  id: string
  meta?: Partial<IMeta>
  originalContent?: string
  quantities?: IKeyTermQuantity<any>[]
  suggestedQuantities?: IKeyTermQuantity<any>[]
  termTypeID: string
}

export interface AutoGeneratedValue<T = any> extends IMetaBase { 
  moreInfo?: any
  status?: AiGeneratedStatus 
  success: boolean
  value: T
}

export interface IDocumentMeta extends IMetaBase {
  autoGenerated?: Record<DocumentMetadataExtractionField, AutoGeneratedValue>
  contactIDs?: string[]
  updated?: number
}

export interface UnitSum {
  sum: number
  unitName: string
}

export interface TermSum {
  sum: number
  termTypeID: string
  unitSums: UnitSum[]
}

export interface IDBDocument extends Omit<IDocument, 'latestVersion' | 'related'> {
  related?: string[]
}

export interface IDocumentAttachment {
  created: number
  createdBy: string
  id: string
  name: string
  size: number
}

export interface IRelatedDocument {
  contractType?: IContractType
  endTime?: number
  id: string
  meta?: IDocumentMeta
  startTime?: number
  title: string
}

export interface IDocument {
  attributeKeyTermCount?: number
  attachments?: IDocumentAttachment[]
  bytes?: number
  // Authoritative per-user capability flags from GET /docs/:id (computed by the
  // backend access helpers). Gate Edit/Delete affordances on these — do NOT
  // re-derive edit rights from editUserIds, which is meaningless for grouped users.
  canEdit?: boolean
  canDelete?: boolean
  content?: string
  contractType?: IContractType
  contractTypeId?: string
  creatorId?: number | null
  editUserIds?: number[]
  accessUserIds?: number[]
  description?: string
  documentKeyTermCount?: number
  endTime?: number
  id: string
  info?: IDocumentInfo[]
  keyTerms?: IKeyTerm[]
  keyTermsCount?: number
  latestVersion?: IDocumentVersion
  meta?: IDocumentMeta
  pageCount: number
  // permissions?: {
  //   locked: IPermissionAssignee[]
  //   meta: IBasePermissionMeta
  //   permissions: IPermissionRecord[]
  //   // permissions: {
  //   //   groups: Record<Permission, string[]>
  //   //   users: Record<Permission, string[]>
  //   // }
  // }
  // related?: { contractType?: string id: string title: string }[]
  relatedCount?: number
  relatedIds?: string[]
  selectedVersionId?: string
  startTime?: number
  termLengthInDays?: number
  termTypeSums?: TermSum[]
  title?: string
  users?: string[]
  versions: IDocumentVersion[]
}

export interface ITermAnalyticsSearchResult extends Omit<IDocument, 'contractTypeId' | 'pageCount' | 'users' | 'versions'> {
  attributeKeyTerms: IKeyTerm[]
  documentKeyTerms: IKeyTerm[]
  id: string
  keyTerms: IKeyTerm[]
}

export interface IIssue {
  closed: boolean
  commentCount: number
  created: number
  creatorName: string
  creatorUserID: string
  description?: string
  documentId: string
  id: string
  lastStatus: string
  name: string
  updated: number
}

export interface IRawContractType {
  id: string
  termTypeIDs: string[]
  title: string
}

export interface IContractType {
  autoGenerated?: boolean
  id: string
  terms: ITerm[]
  title: string
}

export interface ITerm {
  docCount?: number
  id: string
  isAttribute?: boolean
  title: string
}

export interface IGroupedKeyTerm {
  termType: ITerm
  keyTerms: IKeyTerm[]
}

export interface IUserMeta {
  dashboards?: { default: string }
  dontShow?: Record<string, boolean>
}

export type MfaMethod = 'TOTP' | 'SMS' | 'NOMFA' | 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA'

export interface IThirdPartyNode<T = IThirdPartyNodeValue[]> {
  description?: string
  directory?: boolean
  edge?: boolean
  label: string
  loading?: boolean
  noChildren?: boolean
  owner: string
  parent: string
  path: string
  provider: ThirdPartyProvider
  title?: string
  value?: T
}

export type ThirdPartyPermission = 'manageHidden' | 'visible'

export type ThirdPartyGlobalPermission = 'manageHidden' | 'hidden'

export type ThirdPartyPermissionRecord = Record<ThirdPartyPermission, string[]>

export type ThirdPartyGlobalPermissionRecord = Record<ThirdPartyGlobalPermission, string[]>

export interface IThirdPartyPermission<T = ThirdPartyPermissionRecord> {
  path: string
  permissions: T
  provider: ThirdPartyProvider
}

export type ThirdPartyProvider = 'all' | 'instagram' | 'facebook' | 'tiktok' | 'twitter'

export interface IThirdPartyProviderCredentials {
  expires: number
  token: string
  tokenType?: string
  userId?: any
  }
  
export type ThirdPartyProviderCredentialsRecord = Record<
  ThirdPartyProvider,
  IThirdPartyProviderCredentials
>

export type ThirdPartyMediaType = 'image' | 'video'

export type ThirdPartyNodeValueType = ThirdPartyMediaType | 'field' | 'value' | 'message' | 'post'

export interface IThirdPartyNodeValue {
  href?: string
  label: string
  meta?: any
  thumbnail?: string
  timestamp?: number
  type: ThirdPartyNodeValueType
  value: any
}

export interface IThirdPartyMedia {
  meta?: any
  timestamp: number
  title?: string
  type: ThirdPartyMediaType
  uri: string
}

export interface IPaginatedResults<T> {
  data: T[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export type DirectMessageMessageType = 'Message' | 'Share'

export interface IDirectMessageMessageContent {
  link?: string
  media?: IThirdPartyMedia[]
  originalOwner?: string
  shareText?: string
  text?: string
}

export interface IDirectMessageMessageReaction {
  reaction: string
  sender: string
}

export interface IDirectMessageMessage {
  content?: IDirectMessageMessageContent
  reactions?: IDirectMessageMessageReaction[]
  removed?: boolean
  sender: string
  timestamp: number
  type: DirectMessageMessageType
}

export interface IDirectMessageParticipant {
  name: string
}

export interface IDirectMessage {
  messages: IDirectMessageMessage[]
  participants: IDirectMessageParticipant[]
  title: string
}

export interface IPostContent {
  links?: string[]
  media?: IThirdPartyMedia[]
  text?: string
}

export interface IPost {
  content: IPostContent
  creator?: string
  tags?: string[]
  timestamp: number
  title?: string
}

export type FeatureId = keyof FeatureSettingsRegistrar


export interface FeatureSettingsRegistrar {
  dashboards: DashboardsFeatureSettings
  'aiintel': Record<string, any>
  'ai-metadata': Record<string, any>
  'ai-metadata-upload': Record<string, any>
  'data-privacy': Record<string, any>
  legaliq: Record<string, any>
  power_bi: Record<string, any>
  smartterms: Record<string, any>
  'smartterms-upload': Record<string, any>
  'dashboard-favorite': { id: string }
  // Org-created custom document-terminology options (singular/plural pairs) shown
  // in the Org Settings terminology dropdown alongside the built-in presets.
  'document_terminology_options': {
    options: { key: string; singular: string; plural: string }[]
  }
}

export interface DashboardsFeatureSettings {
  [dashboardId: string]: { showNav?: boolean }
}

export interface IOrganizationFeature<Id extends FeatureId = any> {
  id: FeatureId
  settings?: FeatureSettingsRegistrar[Id]
}

export interface IOrganization {
  disabled?: boolean
  features?: IOrganizationFeature[]
  id: string
  logins?: { clientID: string, serviceName: string }[]
  logos?: { dark?: string, light?: string, pdf?: string }
  name: string
  plan_id?: string
  max_documents?: number
  max_users?: number
  stripe_customer_id?: string
  // Org-configurable document wording (defaults to contract/contracts) + live-call greeting.
  documentTermSingular?: string
  documentTermPlural?: string
  tavusGreeting?: string | null
}

export interface IAutoKeyTermResults {
  keyTerms: IKeyTerm[]
  newTerms: ITerm[]
  pageNumber: number
  pageSize: number
  totalPages: number
  totalRecords: number
}

/**
 * Permissions
 */

// export type Permission = 'document.create' | 'document.delete' | 'document.hidden' | 'document.read' | 'document.update' | PermissionRoleId
// export type PermissionResource = 'docs'
// export type PermissionRoleId = string
export type PermissionGroup = `group:${string}`
export type Permission = string
export type PermissionResource = `resource:${string}`
export type PermissionRole = `role:${string}`
export type PermissionUser = `user:${string}`
export type PermissionRecordValue = PermissionUser | PermissionGroup | PermissionRole
export type RolePermissionsValue = Permission | PermissionRole
export type PermissionToken = PermissionGroup | PermissionResource | PermissionRole | PermissionUser
export type PermissionRecordValuePrefix = 'group' | 'user'
export type PermissionTokenPrefix = 'resource' | 'role' | PermissionRecordValuePrefix

// export interface IPermissionAssignee {
//   groupIds?: string[]
//   id: string
//   type: 'group' | 'user'
// }

// export interface IPermissionRecord {
//   assignee: IPermissionAssignee
//   inherited?: boolean
//   permissions: Permission[]
// } 
export interface IPermissionRecord {
  permission: Permission
  users: PermissionRecordValue[]
}

export interface IBasePermissionMeta extends IMeta {}

// export interface IPermissionResource {
//   id?: string
//   type: PermissionResource
// }

export interface IResourcePolicy {
  // locked?: IPermissionAssignee[]
  locked?: PermissionRecordValue[]
  meta?: IBasePermissionMeta
  permissions: IPermissionRecord[]
  // resource: IPermissionResource
  resource: PermissionResource
} 
// old commit - cd186c7 new commit - ddc9c1a

export interface IRolePermissions {
  meta: IBasePermissionMeta
  name: string
  // permissions: Permission[]
  permissions: RolePermissionsValue[]
}

export interface IPermissionGroup {
  id: string
  meta: IBasePermissionMeta
  name: string
  users: string[]
}

// AKA: Team
export interface IResourceCollection {
  fields: string[]
  groupIds?: string[]// old prop
  id: string
  // members: IPermissionAssignee[]
  meta: IBasePermissionMeta
  name: string
  overwriteExisting?: boolean
  // permissions: IResourceCollectionPermissionRecord[]
  permissions: IPermissionRecord[]
  resources: PermissionResource[]
  searchPipeline: any[]
  searchQuery: IContractSearchQuery
  userIds?: string[]
}

// export interface IResourceCollectionPermissionRecord {
//   permissions: IPermissionRecord[]
//   resourceType: PermissionResource
// }

export type SortOrder = 'asc' | 'desc'

export interface ISectionDescription {
  header: ISectionHeader
  content: string
  subsections: ISectionDescription[]
}

export interface ISectionHeader {
  bullet: string
  bulletParts?: string[]
  bulletDelimiter?: string
  bulletType: SectionHeaderBulletType
  isIndented: boolean
  rawBullet: string
  rawHeader?: string
  title: string
}

export enum SectionHeaderBulletType {
  NONE,
  ROMAN,
  ALPHA,
  NUMERIC,
  UNORDERED,
}

export interface IDocumentChangeEvent {
  changes: { key: string; value: any }[]
  documentId: string
  meta: { createdBy: string; createdOn: number }
  orgId: string
}

export const documentSearchSortyByValues = ['created', 'endTime', 'relevance', 'startTime', 'title'] as const
export type DocumentSearchSortBy = typeof documentSearchSortyByValues[number]

export enum SearchFilterOperator {
  CONTAINS = 'contains',
  EQUALS = 'eq',
  NOT_EQUAL = 'ne',
  LESS_THAN_OR_EQUAL_TO = 'lte',
  GREATER_THAN_OR_EQUAL_TO = 'gte',
  LESS_THAN = 'lt',
  GREATER_THAN = 'gt',
  EXISTS = 'exists',
}

export const SearchFilterOperatorNames = {
  [SearchFilterOperator.EQUALS]: 'is',
  [SearchFilterOperator.CONTAINS]: 'contains',
  [SearchFilterOperator.NOT_EQUAL]: 'is not',
  [SearchFilterOperator.LESS_THAN_OR_EQUAL_TO]: '<=',
  [SearchFilterOperator.GREATER_THAN_OR_EQUAL_TO]: '>=',
  [SearchFilterOperator.LESS_THAN]: '<',
  [SearchFilterOperator.GREATER_THAN]: '>',
  [SearchFilterOperator.EXISTS]: 'exists',
}

export interface ITermSearchFilter {
  disabled?: boolean
  id: string
  operator: SearchFilterOperator
  termId: string
  value: string[]
}

export interface IAnalyticsSearchValue {
  documentIds?: string[]
  excludeFields?: string[]  
  includeFields?: string[]
  matchedTermTypes?: string[]
  searchTerms?: Pick<ITermSearchFilter, 'operator' | 'termId' | 'value'>[]
}

export interface IContractSearchQuery {
  active?: boolean
  contractInformationFilters?: ITermSearchFilter[]
  contractTypes?: string[]
  createdFrom?: number
  createdTo?: number
  documentIds?: string[]
  endFrom?: number
  endTo?: number
  endTimeUnset?: boolean
  excludeFields?: string[]
  expired?: boolean
  includeFields?: string[]
  keyTermFilters?: ITermSearchFilter[]
  limit?: number
  matchedTermTypes?: string[]
  query?: string
  signedAfter?: number
  signedBefore?: number
  sortBy?: DocumentSearchSortBy
  sortOrder?: SortOrder
  startFrom?: number
  startTo?: number
}

export interface IAtlasSearchMatch {
  type: 'hit' | 'text'
  value: string
}

export interface IAtlasSearchHighlight {
  path: string
  score: number
  texts: IAtlasSearchMatch[]
}

export interface IContractSearchResultRelatedDocument extends Pick<IDocument, 'id' | 'title' | 'description' | 'endTime' | 'meta' | 'startTime' | 'latestVersion'> {}

export interface IContractSearchResult {
  bytes?: number
  // content: string
  contractType?: string
  created: number
  createdBy?: string
  createdByName?: string
  description?: string
  endTime?: number
  id: string
  highlight?: IAtlasSearchHighlight
  latestVersion?: IDocumentVersion
  pageCount?: number
  related?: string[]
  // related?: IContractSearchResultRelatedDocument[]
  startTime?: number
  termTypeGroupID?: string
  title: string
}

export type FacetField = 'bytes' | 'cb' | 'created' | 'contractType' | 'endTime' | 'id' | 'kt' | 'pageCount' | 'startTime'

export type FacetAggregate = 'avg' | 'count' | 'first' | 'max' | 'min' | 'none' | 'sum' | 'unique'

export type ProcessingJobStatus = 'initiated' | 'uploading' | 'uploaded' | 'processing' | 'processed' | 'failed' | 'ai-processing' | 'ai-processed' | 'completed'

export interface IProcessingJob<T> {
  data?: T
  id: string 
  meta: { created: number; updated: number }
  orgId: string
  progress?: number
  status: ProcessingJobStatus
  userId: string
}

export interface IUploadDocument {
  bytes: number
  id: string
  pageCount?: number
  parentId: string
  title: string
  versionTitle: string
}

export namespace Chart {
  export type Aggregation = 'avg' | 'count' | 'first' | 'max' | 'min' | 'none' | 'sum' | 'unique'

  export interface Configuration {
    group: string[]
    id: string
    meta?: IMeta
    options: Highcharts.Options
    searchQuery: IContractSearchQuery
    series: Series[]
  }

  export type SeriesType = keyof SeriesRegistry

  export interface BarSeries {
    groupAxis?: 'x' | 'y'
    values: string[]
  }

  export interface PieSeries {
    groupAxis?: 'x' | 'y'
    values: string[]
  }

  export interface SplineSeries {
    groupAxis?: 'x' | 'y'
    values: string[]
  }

  export interface StaticLineSeries {
    axis: 'xAxis' | 'yAxis'
    name?: string
    value: any
  }

  export interface SeriesRegistry {
    column: BarSeries
    pie: PieSeries
    spline: SplineSeries
    staticLine: StaticLineSeries
  }

  export interface SeriesTypeHighchartsOptionsRegistry extends Record<keyof SeriesRegistry, Highcharts.SeriesOptions> {
    column: Highcharts.SeriesColumnOptions
    pie: Highcharts.SeriesPieOptions
    spline: Highcharts.SeriesSplineOptions
  }

  export type Series<T extends keyof SeriesRegistry = any> = SeriesRegistry[T] & {
    aggregate: Aggregation
    id: string
    notHighchartsSeries?: boolean
    type: keyof SeriesRegistry
  }

  export interface SeriesProps {
    id: string
    onPointClick: (point: Highcharts.Point) => void
  }
}

export namespace Dashboard {
  export type Type = 'dashboard' | 'folder' | 'powerbi' | 'plotly'

  export interface DashboardMetaRegistry extends Record<Type, IMeta> {
    dashboard: IMeta
    plotly: IPlotlyDashboardMeta
    powerbi: IPowerBiDashboardMeta
  }

  export interface IPowerBiDashboardMeta extends IMeta {
    embedUrl?: string
    reportId?: string
    workspaceId?: string
  }

  export interface IPlotlyDashboardMeta extends IMeta {
    url: string
  }

  export interface Config<T extends keyof DashboardMetaRegistry = any> {
    children?: Config[]
    id: string
    meta?: DashboardMetaRegistry[T]
    name: string
    parentId?: string
    reports?: string[]
    type: T
  }
}

export interface PowerBiEmbedToken {
  expiration: string
  token: string
  tokenId: string
}

export type PowerBiReportAccessLevel = 'Create' | 'Edit' | 'View'

export interface IContact {
  address?: IContactAddress[]
  company?: string
  department?: string
  email?: IContactData[]
  firstName?: string
  id: string
  lastName?: string
  meta?: IMeta
  phone?: IContactData[]
  title?: string
}

export interface IContactAddress {
  address1?: string
  address2?: string
  city?: string
  country?: string
  state?: string
  zip?: string
}

export interface IContactData {
  name?: string
  value: string
}

export enum DocumentPreviewType {
  ORIGINAL = 'original',
  HTML = 'html',
  TEXT = 'txt'
}

export type TermSearchSort = 'a-z' | 'z-a' | 'popularity'

export enum IssueEventType {
  OPENED = "open",
  CLOSED = "closed",
  COMMENT = "comment",
  STATUS = "status"
}

export interface IIssueEvent {
  content?: string
  created: number
  creatorUserID: string
  creatorName: string
  external?: boolean // Created by an internal or external user?
  fromContactID?: string // Associated with a contact?
  fromContactName?: string
  id: string
  type: IssueEventType
}

export interface ITermSheetTemplate {
  contractType: string
  id: string
  name: string
  termTypes: ITerm[]
}

export type AiGeneratedStatus = 'accepted' | 'duplicate' | 'rejected' | 'edited'

export interface ISuggestedKeyTerm extends Pick<IKeyTerm, 'autoExtracted' | 'content' | 'id' | 'originalContent' |  'versionId'> {
  category: string
  documentId: string
  meta: IMetaBase
  quantities: IKeyTermQuantity<any>[]
  versionId?: string
  status?: AiGeneratedStatus
}

/**
 * Jobs
 */

export type JobStatusBase = 'initial' | 'complete' | 'error' | 'deleting'
export type AiJobStatus = JobStatusBase | 'processing'
export type DocumentMetadataStatus = JobStatusBase | 'processing'
export type DocumentUploadStatus = AiJobStatus | 'processingQueued' | 'processing' | 'processingComplete' | 'uploading' | 'uploadComplete'
export type JobStatus = AiJobStatus | DocumentUploadStatus

export type JobType = keyof JobMetaRegistry

export interface IJob<Type extends JobType> {
  data?: JobDataRegistry[Type]
  entityId: string
  errorDetails?: Record<string, any>
  id: string
  meta?: JobMetaRegistry[Type]
  name: string
  orgId: string
  progress?: number
  status: JobStatusRegistry[Type]
  type: Type
  userId: string
}

export interface JobDataRegistry {
  documentUpload: IDocumentUploadData
  documentKeyTermExtraction: IDocumentKeyTermExtractionData
  documentMetadataExtraction: IDocumentMetadataExtractionData
  sentenceKeyTermExtraction: ISentenceKeyTermExtractionData
}

export interface JobMetaRegistry {
  documentUpload: IDocumentUploadMeta
  documentKeyTermExtraction: IDocumentKeyTermExtractionMeta
  documentMetadataExtraction: IDocumentMetadataExtractionMeta
  sentenceKeyTermExtraction: ISentenceKeyTermExtractionMeta
}

export interface JobStatusRegistry {
  documentUpload: DocumentUploadStatus
  documentKeyTermExtraction: AiJobStatus
  documentMetadataExtraction: DocumentMetadataStatus
  sentenceKeyTermExtraction: AiJobStatus
}

export interface IJobMetaBase {
  process: { arn: string }
}

export interface IDocumentUploadMeta extends IJobMetaBase {
  ecs: {
    clusterArn: string
    taskArn: string
  }
}

export interface IDocumentKeyTermExtractionMeta extends IJobMetaBase {}

export interface IDocumentMetadataExtractionMeta extends IJobMetaBase {}

export interface ISentenceKeyTermExtractionMeta extends IJobMetaBase {}

export interface IDocumentUploadData {
  bytes: number
  createdByName: string
  createdById: string
  createdOn: number
  documentUploadId: string
  documentUploadPath: string
  documentUploadUrls: string[]
  pageCount?: number
  parentId?: string
  textUploadId?: string
  textUploadPath?: string
  textUploadUrls?: string[]
  title: string
  type: string
  versionTitle?: string
}

export interface IDocumentKeyTermExtractionData {}

export interface ISentenceKeyTermExtractionData {}

export type DocumentMetadataExtractionField = 'contractType' | 'description' | 'endTime' | 'startTime'

export interface IDocumentMetadataExtractionData {
  fields: DocumentMetadataExtractionField[]
}

export interface ITreeNode {
  attribute?: boolean
  children?: ITreeNode[]
  contractTypeIds?: string[]
  id: string
  label: string
  parentIds?: string[]
  path: string
}

/** Websocket Interfaces for Client */

export interface IClientWebsocketMessage<Action extends ClientWebsocketAction>  {
  action: ClientWebsocketAction
  payload: IClientWebsocketActionRegistry[Action]
}

export type ClientWebsocketAction = keyof IClientWebsocketActionRegistry

export interface IClientWebsocketActionRegistry {
  /**
   * {
   *  action: 'heartbeat',
   *  payload: 'pong'
   * }
   */
  heartbeat: 'pong'

  /**
   * {
   *  action: 'subscribe',
   *  payload: [{
   *    entityType: 'jobs',
   *    filters: [
   *      { type: ['documentUpload'], userId: ['myUserid'] }
   *    ]
   *  }]
   * }
   */
  subscribe: IWebsocketSubscribePayload[]
}

export type WebsocketSubscriptionTopic = keyof WebsocketSubscriptionFilterRegistry

/**
 * To subscribe...
 * 
 * {
 *  entityType: 'jobs',
 *  filters: [
 *    { type: ['documentUpload'], userId: ['myUserid'] },
 *    { type: ['documentMetadataExtraction', 'documentKeyTermExtraction'] }
 *  ]
 * }
 * 
 * To unsubscribe...
 * {
 *  entityType: 'jobs'
 *  filters: [] // You can provide an empty array or not filter property
 * }
 */
export interface IWebsocketSubscribePayload<T extends WebsocketSubscriptionTopic = any> {
  filters: WebsocketSubscriptionFilterRegistry[T][]
  topic: T
}

export type SubscriptionFilter<T> = Record<keyof T, any[]>

export interface WebsocketSubscriptionFilterRegistry {
  // documentAction: ('created' | 'edited' | 'deleted')[]

  /**
   * Filter the documents returned based on criteria of the document.
   * 
   * Example:
   * [
   *  { id: ['documentOneId'] },
   * ]
   */
  // documents: SubscriptionFilter<IDocument>[]

  /**
   * Filter the jobs returned based on criteria of the job.
   * 
   * Example:
   * [
   *  { type: ['documentUpload'], userId: ['myUserid'] },
   *  { type: ['documentMetadataExtraction', 'documentKeyTermExtraction'] }
   * ]
   */
  jobs: SubscriptionFilter<IJob<any>>[]
}

/** 
 * Websocket Interfaces for Server 
 * 
 * Example:
 * 
 * The client has a subscription for `documentUpload` jobs that belong to their userId. The
 * server would send a message to that client like this...
 * 
 * {
 *  type: 'subscribe',
 *  payload: [
 *    { topic: 'jobs', data: [Job1, Job2] }
 *  ]
 * }
 * */

export interface IServerWebsocketMessage<T extends ServerWebsocketType> {
  type: T
  payload: IServerWebsocketTypeRegistry[T]
}

export type ServerWebsocketType = keyof IServerWebsocketTypeRegistry

export interface IServerWebsocketTypeRegistry {
  heartbeat: 'ping'
  
  subscribe: IServerWebsocketSubscription[]
}

export interface IServerWebsocketSubscription<T extends WebsocketSubscriptionTopic = any, Data = any> {
  topic: T
  data: Data
}

export interface IMultipartPart {
  eTag: string
  partNumber: number
}